import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { apiClient } from '@/lib/api-client';
import {
  useFinancialPeriods,
  useCurrentFinancialPeriod,
  useOpenPeriod,
  useClosePeriod,
  useLockPeriod,
  useReopenFinancialPeriod,
  useUpdatePeriod,
  useDeletePeriod,
  type FinancialPeriod,
} from './hooks/useFinancialPeriods';

function formatAmount(amount: number | null): string {
  if (amount == null) return '—';
  return amount.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const STATUS_CONFIG: Record<string, { variant: 'success' | 'warning' | 'default'; label: string }> =
  {
    open: { variant: 'success', label: 'Open' },
    closed: { variant: 'warning', label: 'Closed' },
    locked: { variant: 'default', label: 'Locked' },
  };

/**
 * FinancialPeriodsPage — manage system-wide financial periods.
 *
 * Shows:
 * - Current open period status banner
 * - List of all periods with opening/closing balances and totals
 * - Actions: Open new period, Close, Lock, Reopen
 */
export function FinancialPeriodsPage() {
  const { can } = usePermissions();
  const canManage = can(PERMISSIONS.BILLING_MANAGE_PERIODS);
  const canDelete = can(PERMISSIONS.BILLING_DELETE_PERIOD);

  // ─── Financial Period Enabled Setting ───────────────────────────────────────
  // NOTE: Toggle is managed from General Settings; queries kept for future re-enablement.
  // const billingSettings = useQuery({ ... });
  // const toggleEnabled = useMutation({ ... });

  const periods = useFinancialPeriods();
  const currentPeriod = useCurrentFinancialPeriod();
  const openPeriod = useOpenPeriod();
  const closePeriod = useClosePeriod();
  const lockPeriod = useLockPeriod();
  const reopenPeriod = useReopenFinancialPeriod();
  const updatePeriod = useUpdatePeriod();
  const deletePeriod = useDeletePeriod();

  // ─── Open Period Modal State ────────────────────────────────────────────────
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');
  const [newOpeningBalance, setNewOpeningBalance] = useState('');
  const [newNotes, setNewNotes] = useState('');

  // ─── Close Period Modal State ───────────────────────────────────────────────
  const [closingPeriodId, setClosingPeriodId] = useState<string | null>(null);
  const [closeNotes, setCloseNotes] = useState('');
  const [closeAdjustedBalance, setCloseAdjustedBalance] = useState('');

  // ─── Lock Confirmation State ────────────────────────────────────────────────
  const [lockingPeriodId, setLockingPeriodId] = useState<string | null>(null);

  // ─── Edit Period Modal State ────────────────────────────────────────────────
  const [editingPeriod, setEditingPeriod] = useState<FinancialPeriod | null>(null);
  const [editName, setEditName] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editNotes, setEditNotes] = useState('');

  const handleOpenPeriod = useCallback(() => {
    if (!newName || !newStartDate || !newEndDate) return;
    openPeriod.mutate(
      {
        period_name: newName,
        start_date: newStartDate,
        end_date: newEndDate,
        notes: newNotes || undefined,
        opening_balance: newOpeningBalance ? parseFloat(newOpeningBalance) : undefined,
      },
      {
        onSuccess: () => {
          setShowOpenModal(false);
          setNewName('');
          setNewStartDate('');
          setNewEndDate('');
          setNewOpeningBalance('');
          setNewNotes('');
        },
      },
    );
  }, [newName, newStartDate, newEndDate, newNotes, newOpeningBalance, openPeriod]);

  const handleClosePeriod = useCallback(() => {
    if (!closingPeriodId) return;
    closePeriod.mutate(
      {
        periodId: closingPeriodId,
        notes: closeNotes || undefined,
        adjusted_closing_balance: closeAdjustedBalance
          ? parseFloat(closeAdjustedBalance)
          : undefined,
      },
      {
        onSuccess: () => {
          setClosingPeriodId(null);
          setCloseNotes('');
          setCloseAdjustedBalance('');
        },
      },
    );
  }, [closingPeriodId, closeNotes, closeAdjustedBalance, closePeriod]);

  const handleLockPeriod = useCallback(() => {
    if (!lockingPeriodId) return;
    lockPeriod.mutate(lockingPeriodId, {
      onSuccess: () => setLockingPeriodId(null),
    });
  }, [lockingPeriodId, lockPeriod]);

  const handleEditPeriod = useCallback((period: FinancialPeriod) => {
    setEditingPeriod(period);
    setEditName(period.period_name);
    setEditStartDate(String(period.start_date).split('T')[0]);
    setEditEndDate(String(period.end_date).split('T')[0]);
    setEditNotes(period.notes || '');
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (!editingPeriod || !editName || !editStartDate || !editEndDate) return;
    updatePeriod.mutate(
      {
        periodId: editingPeriod.id,
        period_name: editName,
        start_date: editStartDate,
        end_date: editEndDate,
        notes: editNotes || null,
      },
      {
        onSuccess: () => setEditingPeriod(null),
      },
    );
  }, [editingPeriod, editName, editStartDate, editEndDate, editNotes, updatePeriod]);

  const allPeriods = periods.data?.data ?? [];

  // Determine the last period (most recent by end_date) — only this one can be deleted
  const lastPeriodId =
    allPeriods.length > 0
      ? [...allPeriods].sort((a, b) => (a.end_date > b.end_date ? -1 : 1))[0].id
      : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Financial Periods</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage accounting periods. All financial transactions must fall within an open period.
          </p>
        </div>
        {canManage && (
          <Button
            variant="primary"
            onClick={() => setShowOpenModal(true)}
            disabled={!!currentPeriod.data}
          >
            Open New Period
          </Button>
        )}
      </div>

      {/* Enable/Disable Toggle */}
      {/* Financial Period Enforcement toggle is managed from General Settings */}

      {/* Current Period Status */}
      {currentPeriod.data ? (
        <Card padding="md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/30">
                <svg
                  className="h-5 w-5 text-green-600 dark:text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Current Open Period: {currentPeriod.data.period_name}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(currentPeriod.data.start_date)} —{' '}
                  {formatDate(currentPeriod.data.end_date)}
                  {' · '}Opening Balance: {formatAmount(currentPeriod.data.opening_balance)}
                </div>
              </div>
            </div>
            {canManage && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setClosingPeriodId(currentPeriod.data!.id)}
              >
                Close Period
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/30">
              <svg
                className="h-5 w-5 text-yellow-600 dark:text-yellow-400"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <div>
              <div className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">
                No Open Period
              </div>
              <div className="text-xs text-yellow-600 dark:text-yellow-400">
                Financial transactions are blocked until a period is opened.
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Periods Table */}
      <Card padding="none">
        <div className="border-b border-border px-4 py-3 dark:border-border-dark">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">All Periods</h2>
        </div>
        {periods.isLoading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        ) : allPeriods.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No financial periods have been created yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-gray-50 text-left text-xs font-medium uppercase text-gray-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-400">
                  <th className="px-4 py-2.5">Period</th>
                  <th className="px-4 py-2.5">Dates</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5 text-right">Opening</th>
                  <th className="px-4 py-2.5 text-right">Invoiced</th>
                  <th className="px-4 py-2.5 text-right">Received</th>
                  <th className="px-4 py-2.5 text-right">Credit Notes</th>
                  <th className="px-4 py-2.5 text-right">Write-Offs</th>
                  <th className="px-4 py-2.5 text-right">Closing</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {allPeriods.map((period: FinancialPeriod) => {
                  const config = STATUS_CONFIG[period.status];
                  return (
                    <tr
                      key={period.id}
                      className="border-b border-border last:border-0 dark:border-border-dark"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {period.period_name}
                        </div>
                        {period.notes && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {period.notes}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {formatDate(period.start_date)} — {formatDate(period.end_date)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={config.variant} size="sm">
                          {config.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-gray-100">
                        {formatAmount(period.opening_balance)}
                      </td>
                      <td className="px-4 py-3 text-right text-red-600 dark:text-red-400">
                        {formatAmount(period.total_invoiced)}
                      </td>
                      <td className="px-4 py-3 text-right text-green-600 dark:text-green-400">
                        {formatAmount(period.total_received)}
                      </td>
                      <td className="px-4 py-3 text-right text-green-600 dark:text-green-400">
                        {formatAmount(period.total_credit_notes)}
                      </td>
                      <td className="px-4 py-3 text-right text-green-600 dark:text-green-400">
                        {formatAmount(period.total_write_offs)}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900 dark:text-gray-100">
                        {formatAmount(period.closing_balance)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canManage && (
                          <div className="flex items-center justify-end gap-2">
                            {period.status !== 'locked' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditPeriod(period)}
                              >
                                Edit
                              </Button>
                            )}
                            {period.status === 'open' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setClosingPeriodId(period.id)}
                              >
                                Close
                              </Button>
                            )}
                            {period.status === 'closed' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => reopenPeriod.mutate(period.id)}
                                >
                                  Reopen
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setLockingPeriodId(period.id)}
                                >
                                  Lock
                                </Button>
                              </>
                            )}
                            {period.status === 'locked' && (
                              <span className="text-xs text-gray-400">Sealed</span>
                            )}
                            {canDelete &&
                              period.status !== 'locked' &&
                              period.id === lastPeriodId && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 dark:text-red-400"
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        'Are you sure you want to delete this period? This cannot be undone.',
                                      )
                                    ) {
                                      deletePeriod.mutate(period.id);
                                    }
                                  }}
                                  loading={deletePeriod.isPending}
                                >
                                  Delete
                                </Button>
                              )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Open Period Modal */}
      {showOpenModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="open-period-title"
        >
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-surface-dark">
            <h3
              id="open-period-title"
              className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              Open New Financial Period
            </h3>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="fp-name"
                  className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Period Name
                </label>
                <input
                  id="fp-name"
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g., Q3 2026, July 2026"
                  className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="fp-start"
                    className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Start Date
                  </label>
                  <input
                    id="fp-start"
                    type="date"
                    value={newStartDate}
                    onChange={(e) => {
                      const start = e.target.value;
                      setNewStartDate(start);
                      if (start) {
                        const startDate = new Date(start + 'T00:00:00');
                        const endDate = new Date(startDate);
                        endDate.setFullYear(endDate.getFullYear() + 1);
                        endDate.setDate(endDate.getDate() - 1);
                        setNewEndDate(endDate.toISOString().split('T')[0]);
                      }
                    }}
                    className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                  />
                </div>
                <div>
                  <label
                    htmlFor="fp-end"
                    className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    End Date
                  </label>
                  <input
                    id="fp-end"
                    type="date"
                    value={newEndDate}
                    onChange={(e) => setNewEndDate(e.target.value)}
                    className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="fp-opening"
                  className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Opening Balance{' '}
                  <span className="text-xs font-normal text-gray-400">
                    (leave empty to carry from last closed period)
                  </span>
                </label>
                <input
                  id="fp-opening"
                  type="number"
                  step="0.01"
                  value={newOpeningBalance}
                  onChange={(e) => setNewOpeningBalance(e.target.value)}
                  placeholder="Auto from previous period"
                  className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                />
              </div>
              <div>
                <label
                  htmlFor="fp-notes"
                  className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Notes (optional)
                </label>
                <textarea
                  id="fp-notes"
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  rows={2}
                  placeholder="Optional notes about this period"
                  className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowOpenModal(false);
                  setNewName('');
                  setNewStartDate('');
                  setNewEndDate('');
                  setNewOpeningBalance('');
                  setNewNotes('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleOpenPeriod}
                loading={openPeriod.isPending}
                disabled={!newName || !newStartDate || !newEndDate}
              >
                Open Period
              </Button>
            </div>
            {openPeriod.isError && (
              <p className="mt-3 text-sm text-red-600">
                {(openPeriod.error as Error)?.message || 'Failed to open period'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Close Period Modal */}
      {closingPeriodId && (
        <ClosePeriodModal
          periodId={closingPeriodId}
          closeNotes={closeNotes}
          setCloseNotes={setCloseNotes}
          closeAdjustedBalance={closeAdjustedBalance}
          setCloseAdjustedBalance={setCloseAdjustedBalance}
          onClose={() => {
            setClosingPeriodId(null);
            setCloseNotes('');
            setCloseAdjustedBalance('');
          }}
          onConfirm={handleClosePeriod}
          isPending={closePeriod.isPending}
          error={
            closePeriod.isError
              ? (closePeriod.error as Error)?.message || 'Failed to close period'
              : null
          }
        />
      )}

      {/* Lock Confirmation Modal */}
      {lockingPeriodId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lock-fp-title"
        >
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl dark:bg-surface-dark">
            <h3
              id="lock-fp-title"
              className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100"
            >
              Lock Period Permanently
            </h3>
            <p className="mb-4 text-sm text-red-600 dark:text-red-400">
              This action is irreversible. A locked period cannot be reopened or modified. This is a
              permanent audit seal.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setLockingPeriodId(null)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleLockPeriod} loading={lockPeriod.isPending}>
                Lock Permanently
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Period Modal */}
      {editingPeriod && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-surface-dark">
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              Edit Financial Period
            </h3>
            <div className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Period Name
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={editStartDate}
                    onChange={(e) => setEditStartDate(e.target.value)}
                    className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={editEndDate}
                    onChange={(e) => setEditEndDate(e.target.value)}
                    className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Notes
                </label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={2}
                  className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setEditingPeriod(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveEdit}
                loading={updatePeriod.isPending}
                disabled={!editName || !editStartDate || !editEndDate}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Close Period Modal with Closing Balance Preview ─────────────────────────

function ClosePeriodModal({
  periodId,
  closeNotes,
  setCloseNotes,
  closeAdjustedBalance,
  setCloseAdjustedBalance,
  onClose,
  onConfirm,
  isPending,
  error,
}: {
  periodId: string;
  closeNotes: string;
  setCloseNotes: (v: string) => void;
  closeAdjustedBalance: string;
  setCloseAdjustedBalance: (v: string) => void;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  error: string | null;
}) {
  const { data: preview, isLoading } = useQuery({
    queryKey: ['billing', 'financial-periods', periodId, 'closing-preview'],
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean;
        data: {
          opening_balance: number;
          total_invoiced: number;
          total_received: number;
          total_credit_notes: number;
          total_debit_notes: number;
          total_write_offs: number;
          total_discounts: number;
          total_advances_received: number;
          total_advances_settled: number;
          closing_balance: number;
        };
      }>(`/billing/financial-periods/${periodId}/closing-preview`);
      return response.data.data;
    },
    staleTime: 0,
    refetchOnMount: 'always',
    gcTime: 0,
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="close-fp-title"
    >
      <div className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-xl dark:bg-surface-dark">
        <h3
          id="close-fp-title"
          className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100"
        >
          Close Financial Period
        </h3>
        <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
          Review the period summary and confirm closing. All transactions within this period will be
          frozen.
        </p>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {/* LEFT: Period Summary Breakdown */}
          <div>
            <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Period Summary
            </h4>
            {isLoading ? (
              <div className="flex items-center justify-center rounded-lg bg-gray-50 p-8 dark:bg-gray-800/50">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
                <span className="ml-2 text-sm text-gray-500">Calculating...</span>
              </div>
            ) : preview ? (
              <div className="space-y-1 rounded-lg bg-gray-50 p-4 dark:bg-gray-800/50">
                {/* Opening Balance */}
                <div className="flex justify-between py-1.5 text-sm">
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    Opening Balance
                  </span>
                  <span className="font-semibold text-gray-900 dark:text-gray-100">
                    {preview.opening_balance.toLocaleString('en', { minimumFractionDigits: 2 })}
                  </span>
                </div>

                {/* Debits Section */}
                <div className="border-t border-gray-200 pt-2 dark:border-gray-700">
                  <p className="mb-1 text-xs font-semibold uppercase text-red-600 dark:text-red-400">
                    Debits (Increases)
                  </p>
                  <div className="flex justify-between py-1 text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Invoices</span>
                    <span className="font-medium text-red-600 dark:text-red-400">
                      +{preview.total_invoiced.toLocaleString('en', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Debit Notes</span>
                    <span className="font-medium text-red-600 dark:text-red-400">
                      +
                      {preview.total_debit_notes.toLocaleString('en', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                {/* Credits Section */}
                <div className="border-t border-gray-200 pt-2 dark:border-gray-700">
                  <p className="mb-1 text-xs font-semibold uppercase text-green-600 dark:text-green-400">
                    Credits (Reductions)
                  </p>
                  <div className="flex justify-between py-1 text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Payments Received</span>
                    <span className="font-medium text-green-600 dark:text-green-400">
                      -{preview.total_received.toLocaleString('en', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Credit Notes</span>
                    <span className="font-medium text-green-600 dark:text-green-400">
                      -
                      {preview.total_credit_notes.toLocaleString('en', {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Write-Offs</span>
                    <span className="font-medium text-green-600 dark:text-green-400">
                      -{preview.total_write_offs.toLocaleString('en', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Discounts</span>
                    <span className="font-medium text-green-600 dark:text-green-400">
                      -{preview.total_discounts.toLocaleString('en', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Advances Settled</span>
                    <span className="font-medium text-green-600 dark:text-green-400">
                      -
                      {preview.total_advances_settled.toLocaleString('en', {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                </div>

                {/* Advances Section */}
                <div className="border-t border-gray-200 pt-2 dark:border-gray-700">
                  <p className="mb-1 text-xs font-semibold uppercase text-blue-600 dark:text-blue-400">
                    Advances
                  </p>
                  <div className="flex justify-between py-1 text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Advances Received</span>
                    <span className="font-medium text-blue-600 dark:text-blue-400">
                      {preview.total_advances_received.toLocaleString('en', {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between py-1 text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Advances Balance</span>
                    <span className="font-medium text-blue-600 dark:text-blue-400">
                      {(
                        preview.total_advances_received - preview.total_advances_settled
                      ).toLocaleString('en', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>

                {/* Closing Balance */}
                <div className="border-t-2 border-gray-300 pt-2 dark:border-gray-600">
                  <div className="flex justify-between py-1.5 text-sm">
                    <span className="font-bold text-gray-900 dark:text-gray-100">
                      Closing Balance
                    </span>
                    <span
                      className={`font-bold ${preview.closing_balance > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}
                    >
                      {preview.closing_balance.toLocaleString('en', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg bg-gray-50 p-6 text-center text-sm text-gray-500 dark:bg-gray-800/50">
                Unable to load summary
              </div>
            )}
          </div>

          {/* RIGHT: Actions */}
          <div>
            <h4 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Closing Details
            </h4>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="close-fp-balance"
                  className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Adjusted Closing Balance
                </label>
                <input
                  id="close-fp-balance"
                  type="number"
                  step="0.01"
                  value={closeAdjustedBalance}
                  onChange={(e) => setCloseAdjustedBalance(e.target.value)}
                  placeholder={
                    preview ? `${preview.closing_balance.toFixed(2)}` : 'System will calculate'
                  }
                  className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                />
                <p className="mt-1 text-xs text-gray-400">
                  Leave empty to use the system-calculated balance shown on the left.
                </p>
              </div>
              <div>
                <label
                  htmlFor="close-fp-notes"
                  className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Notes (optional)
                </label>
                <textarea
                  id="close-fp-notes"
                  value={closeNotes}
                  onChange={(e) => setCloseNotes(e.target.value)}
                  rows={4}
                  placeholder="Add any notes about this period closing..."
                  className="block w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-border-dark dark:bg-surface-dark-secondary dark:text-gray-100"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            loading={isPending}
            disabled={isPending || isLoading}
          >
            Close Period
          </Button>
        </div>
        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
      </div>
    </div>
  );
}
